"use client";

/**
 * <ConsentBanner> — 첫 방문 시 화면 하단에 표시되는 동의 배너 (Phase 0).
 *
 * 사용자 액션:
 *   - "모두 동의" → analytics + marketing 모두 ON
 *   - "필수만 동의" → 모두 OFF (필수만)
 *   - "세부 설정" → ConsentModal 열림
 *
 * 표시 조건: ConsentContext.needsBanner === true (한 번도 동의 안 한 상태).
 */

import { useState } from "react";
import { Cookie, ChevronRight } from "lucide-react";
import { useConsent } from "@/contexts/consent-context";
import { ConsentModal } from "./consent-modal";

export function ConsentBanner({ brandName = "불스리서치", biteSurface = false }: { brandName?: string; biteSurface?: boolean }) {
  const { needsBanner, modalOpen, openModal, acceptAll, rejectOptional, advertising } = useConsent();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const choose = async (action: () => Promise<void>) => {
    setSaving(true); setError("");
    try { await action(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "동의를 저장하지 못했습니다. 다시 시도해주세요."); }
    finally { setSaving(false); }
  };

  return (
    <>
      {needsBanner && !biteSurface && (
        <div className="bs-consent-banner" role="dialog" aria-label="개인정보 처리 동의">
          <div className="bs-consent-banner-inner">
            <div className="bs-consent-banner-content">
              <div className="bs-consent-banner-head">
                <Cookie size={16} className="bs-consent-banner-icon" />
                <strong>개인정보 처리 동의</strong>
              </div>
              <p className="bs-consent-banner-text">
                {biteSurface ? (
                  <>분석에 동의하면 방문 경로와 보고서 이용 및 결제를 연결하며, 로그인 시 계정과 이메일로 연결해 관리자만 확인합니다. 동의하지 않아도 이용할 수 있으며, 개인 식별자를 제외한 이용 횟수와 결제 합계는 집계합니다. {advertising?.available && "광고 측정에 동의하면 Meta에 광고 클릭 식별자와 IP 주소, 브라우저 정보 및 구매 시각과 금액을 보내 광고 성과를 측정하고 광고를 최적화합니다. 보관 기간과 철회 방법은 세부 설정에서 확인할 수 있습니다. "}</>
                ) : (
                  <>{brandName}는 서비스 운영에 필요한 정보 외에 사용자의 행동 패턴을 분석합니다. 분석 결과는 개인화된 기업 추천과 리포트 품질 개선에 활용합니다.</>
                )}
                자세한 내용은{" "}
                <a href="/terms" className="bs-consent-banner-link">이용약관</a>
                {"과 "}
                <a href="/privacy" className="bs-consent-banner-link">개인정보처리방침</a>
                에서 확인하세요.
              </p>
              {error && <p className="bs-consent-banner-text" role="alert">{error}</p>}
            </div>
            <div className="bs-consent-banner-actions">
              <button
                type="button"
                className="bs-consent-btn bs-consent-btn-ghost"
                onClick={() => { void choose(rejectOptional); }}
                disabled={saving}
              >
                필수만 동의
              </button>
              <button
                type="button"
                className="bs-consent-btn bs-consent-btn-ghost"
                onClick={openModal}
                disabled={saving}
              >
                세부 설정 <ChevronRight size={12} />
              </button>
              <button
                type="button"
                className="bs-consent-btn bs-consent-btn-primary"
                onClick={() => { void choose(acceptAll); }}
                disabled={saving || (biteSurface && !advertising)}
              >
                모두 동의
              </button>
            </div>
          </div>
        </div>
      )}
      {modalOpen && <ConsentModal biteSurface={biteSurface} />}
    </>
  );
}
